import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Pagination, Response } from '../../../../_shared/models';
import {
  WorkOrderPaginationRequest,
  TodayWorkshopEventResponse,
  TodayWorkshopEventsPaginationRequest,
  WorkshopEventResponse,
  WorkshopEventScheduleResponse,
  WorkshopEventsByDateRequest,
  WorkshopEventsPreviewRequest
} from '../models/work-order.models';
import { UpdateWorkOrderDetailsRequest, WorkOrderItem, WorkOrderByIdResponse, UpdateWorkOrderEditRequest, ScheduleWorkOrderRequest, DragAndDropEventRequest, WorkOrderFaultImage } from '../models/work-orders-overview.models';
import { BreakdownAnalysisResponse } from '../../maintenance-overview/models/maintenance-overview.model';
import { WorkOrderSourceLabels, WorkOrderPriorityLabels, WorkOrderStatusLabels } from '../enums/work-order.enum';
import { WORK_ORDERS_MOCK, WORK_ORDER_BREAKDOWN_MOCK, OVERDUE_BREAKDOWN_MOCK, IN_PROGRESS_BREAKDOWN_MOCK, AVG_RESOLUTION_BREAKDOWN_MOCK } from '../mockdata/work-order-mock-data';
import { delay } from '../../../../_shared/helpers/delay.helper';
import { environment } from '../../../../../environments/environment';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class WorkOrdersOverviewService {
  private http: HttpClient = inject(HttpClient);
  private transloco = inject(TranslocoService);

  async getWorkOrdersAsync(request: WorkOrderPaginationRequest, cancellation: Subject<void>): Promise<Response<Pagination<WorkOrderItem[]>>> {
    return firstValueFrom(
      this.http.post<Response<Pagination<WorkOrderItem[]>>>(`${environment.apiEndpoint}/workorder/paginated-work-orders`, request).pipe(takeUntil(cancellation))
    );
  }

  async getWorkOrderByIdAsync(workOrderId: number, cancellation: Subject<void>): Promise<Response<WorkOrderByIdResponse>> {
    return firstValueFrom(
      this.http.get<Response<WorkOrderByIdResponse>>(`${environment.apiEndpoint}/WorkOrder/${workOrderId}`).pipe(takeUntil(cancellation))
    );
  }

  async getWorkOrderFaultImagesAsync(workOrderNumber: string, cancellation: Subject<void>): Promise<Response<WorkOrderFaultImage[]>> {
    return firstValueFrom(
      this.http.get<Response<WorkOrderFaultImage[]>>(`${environment.apiEndpoint}/WorkOrder/fault-images/${encodeURIComponent(workOrderNumber)}`).pipe(takeUntil(cancellation))
    );
  }

  async getWorkOrderFaultImageDownloadLinkAsync(workOrderNumber: string, reference: string, cancellation: Subject<void>): Promise<Response<string>> {
    return firstValueFrom(
      this.http.get<Response<string>>(`${environment.apiEndpoint}/WorkOrder/fault-images/${encodeURIComponent(workOrderNumber)}/download/${encodeURIComponent(reference)}`).pipe(takeUntil(cancellation))
    );
  }

  async scheduleWorkOrderAsync(request: ScheduleWorkOrderRequest, cancellation: Subject<void>): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.post<Response<boolean>>(`${environment.apiEndpoint}/workorder/schedule-work-order`, request).pipe(takeUntil(cancellation))
    );
  }

  async updateWorkOrderEditAsync(request: UpdateWorkOrderEditRequest, cancellation: Subject<void>): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.put<Response<boolean>>(`${environment.apiEndpoint}/WorkOrder/update-workorder`, request).pipe(takeUntil(cancellation))
    );
  }

  async deleteWorkOrderAsync(workOrderId: number, cancellation: Subject<void>): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.delete<Response<boolean>>(`${environment.apiEndpoint}/WorkOrder/${workOrderId}`).pipe(takeUntil(cancellation))
    );
  }

  /**
   * Export work orders to CSV/Excel
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async exportWorkOrdersAsync(request: WorkOrderPaginationRequest, cancellation: Subject<void>): Promise<Blob> {
    await delay(1000);

    const headers = 'Work Order ID,Asset,Source,Work Type,Workshop,Estimated Cost,Estimated Time,Priority,Scheduled Date,Status\n';
    const rows = WORK_ORDERS_MOCK.map(wo => {
      const scheduledDate = wo.scheduledDate ? new Date(wo.scheduledDate).toLocaleDateString() : '';
      return `${wo.workOrderId},${wo.assetName},${this.transloco.translate(WorkOrderSourceLabels[wo.source])},${wo.workType},${wo.workshopName},${wo.estimatedCost},${wo.estimatedTime},${this.transloco.translate(WorkOrderPriorityLabels[wo.priority])},${scheduledDate},${this.transloco.translate(WorkOrderStatusLabels[wo.status])}`;
    }).join('\n');

    const csvContent = headers + rows;
    return new Blob([csvContent], { type: 'text/csv' });
    // return firstValueFrom(this.http.post(`${environment.apiEndpoint}/work-orders/export`, request, { responseType: 'blob' }).pipe(takeUntil(cancellation)));
  }

  /**
   * Update work order details (service type, priority, comment, parts)
   */
  async updateWorkOrderDetailsAsync(request: UpdateWorkOrderDetailsRequest, cancellation: Subject<void>): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.put<Response<boolean>>(`${environment.apiEndpoint}/workorder/update-workorder`, request).pipe(takeUntil(cancellation))
    );
  }

  // ============================================
  // KPI Breakdown Modal Methods
  // ============================================

  /**
   * Get work order breakdown data for KPI modal
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async getWorkOrderBreakdownAsync(cancellation: Subject<void>): Promise<Response<BreakdownAnalysisResponse[]>> {
    await delay(500);

    return Promise.resolve({
      data: WORK_ORDER_BREAKDOWN_MOCK
    } as Response<BreakdownAnalysisResponse[]>);

    // Uncomment when backend API is ready
    // return firstValueFrom(
    //   this.http.get<Response<BreakdownAnalysisResponse[]>>(`${environment.apiEndpoint}/work-orders/breakdown`).pipe(takeUntil(cancellation))
    // );
  }

  /**
   * Get overdue breakdown data for KPI modal
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async getOverdueBreakdownAsync(cancellation: Subject<void>): Promise<Response<BreakdownAnalysisResponse[]>> {
    await delay(500);

    return Promise.resolve({
      data: OVERDUE_BREAKDOWN_MOCK
    } as Response<BreakdownAnalysisResponse[]>);

    // Uncomment when backend API is ready
    // return firstValueFrom(
    //   this.http.get<Response<BreakdownAnalysisResponse[]>>(`${environment.apiEndpoint}/work-orders/overdue-breakdown`).pipe(takeUntil(cancellation))
    // );
  }

  /** Get in-progress breakdown data for KPI modal
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async getInProgressBreakdownAsync(cancellation: Subject<void>): Promise<Response<BreakdownAnalysisResponse[]>> {
    await delay(500);

    return Promise.resolve({
      data: IN_PROGRESS_BREAKDOWN_MOCK
    } as Response<BreakdownAnalysisResponse[]>);

    // Uncomment when backend API is ready
    // return firstValueFrom(
    //   this.http.get<Response<BreakdownAnalysisResponse[]>>(`${environment.apiEndpoint}/work-orders/in-progress-breakdown`).pipe(takeUntil(cancellation))
    // );
  }

  /** Get average resolution breakdown data for KPI modal
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async getAverageResolutionBreakdownAsync(cancellation: Subject<void>): Promise<Response<BreakdownAnalysisResponse[]>> {
    await delay(500);

    return Promise.resolve({
      data: AVG_RESOLUTION_BREAKDOWN_MOCK
    } as Response<BreakdownAnalysisResponse[]>);
    // Uncomment when backend API is ready
    // return firstValueFrom(
    //   this.http.get<Response<BreakdownAnalysisResponse[]>>(`${environment.apiEndpoint}/work-orders/average-resolution-breakdown`).pipe(takeUntil(cancellation))
    // );
  }

  // ============================================
  // Workshop Calendar Methods
  // ============================================

  async getTodayWorkshopEventsAsync(request: TodayWorkshopEventsPaginationRequest, cancellation: Subject<void>): Promise<Response<Pagination<TodayWorkshopEventResponse[]>>> {
    return firstValueFrom(
      this.http.post<Response<Pagination<TodayWorkshopEventResponse[]>>>(`${environment.apiEndpoint}/workorder/workshop-calendar/today`, request).pipe(takeUntil(cancellation))
    );
  }

  async getWorkshopEventsPreviewAsync(request: WorkshopEventsPreviewRequest, cancellation: Subject<void>): Promise<Response<WorkshopEventResponse[]>> {
    return firstValueFrom(
      this.http.post<Response<WorkshopEventResponse[]>>(`${environment.apiEndpoint}/workorder/workshop-calendar/preview`, request).pipe(takeUntil(cancellation))
    );
  }

  async getWorkshopEventsByDateAsync(request: WorkshopEventsByDateRequest, cancellation: Subject<void>): Promise<Response<WorkshopEventResponse[]>> {
    return firstValueFrom(
      this.http.post<Response<WorkshopEventResponse[]>>(`${environment.apiEndpoint}/workorder/workshop-calendar/date`, request).pipe(takeUntil(cancellation))
    );
  }

  async getWorkshopEventScheduleByWorkOrderIdAsync(workOrderId: number, cancellation: Subject<void>): Promise<Response<WorkshopEventScheduleResponse | null>> {
    return firstValueFrom(
      this.http.get<Response<WorkshopEventScheduleResponse | null>>(`${environment.apiEndpoint}/workorder/workshop-calendar/event-schedule/${workOrderId}`).pipe(takeUntil(cancellation))
    );
  }

  async rescheduleEventAsync(request: DragAndDropEventRequest, cancellation: Subject<void>): Promise<Response<void>> {
    return firstValueFrom(
      this.http.post<Response<void>>(`${environment.apiEndpoint}/workorder/workshop-calendar/event-reschedule`, request).pipe(takeUntil(cancellation))
    );
  }
}
